const BRIDGE_SOURCE = 'huay-firebase-auth-bridge'
const CLIENT_SOURCE = 'huay-firebase-auth-client'
const DEFAULT_BRIDGE_URL = 'https://otp.huaytded365day.com/firebase-auth-bridge'

type BridgeReadyMessage = { source: typeof BRIDGE_SOURCE; type: 'READY' }
type BridgeSentMessage = { source: typeof BRIDGE_SOURCE; type: 'PHONE_AUTH_SENT'; payload?: { verificationId?: string } }
type BridgeVerifiedMessage = { source: typeof BRIDGE_SOURCE; type: 'PHONE_AUTH_VERIFIED'; payload?: { uid?: string; phoneNumber?: string | null } }
type BridgeErrorMessage = { source: typeof BRIDGE_SOURCE; type: 'ERROR'; payload?: { message?: string; code?: string } }
type BridgeMessage = BridgeReadyMessage | BridgeSentMessage | BridgeVerifiedMessage | BridgeErrorMessage

interface SendOptions {
  tenantId: string
  phoneNumber: string
  apiBaseUrl?: string
  timeoutMs?: number
}

interface PersistedBridgeSession {
  tenantId: string
  phoneNumber: string
  apiBaseUrl: string
  verificationId: string
  createdAt: number
}

let bridgeWindow: Window | null = null
let bridgeOrigin = ''
const BRIDGE_SESSION_STORAGE_KEY = 'huay-firebase-auth-bridge-session'
const BRIDGE_SESSION_TTL_MS = 10 * 60 * 1000

function getBridgeUrl() {
  return ((import.meta.env as any).VITE_FIREBASE_AUTH_BRIDGE_URL as string) || DEFAULT_BRIDGE_URL
}

function loadPersistedBridgeSession(): PersistedBridgeSession | null {
  try {
    const raw = window.sessionStorage.getItem(BRIDGE_SESSION_STORAGE_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as PersistedBridgeSession
    if (
      !parsed?.tenantId
      || !parsed?.phoneNumber
      || !parsed?.verificationId
      || !parsed?.createdAt
      || (Date.now() - parsed.createdAt) > BRIDGE_SESSION_TTL_MS
    ) {
      clearPersistedBridgeSession()
      return null
    }
    return parsed
  } catch {
    return null
  }
}

function savePersistedBridgeSession(session: PersistedBridgeSession) {
  try {
    window.sessionStorage.setItem(BRIDGE_SESSION_STORAGE_KEY, JSON.stringify(session))
  } catch {
    // Ignore storage failures and rely on the live popup state.
  }
}

function clearPersistedBridgeSession() {
  try {
    window.sessionStorage.removeItem(BRIDGE_SESSION_STORAGE_KEY)
  } catch {
    // Ignore storage cleanup failures.
  }
}

export function shouldUseFirebaseAuthBridge() {
  const enabled = ((import.meta.env as any).VITE_ENABLE_FIREBASE_AUTH_BRIDGE ?? 'true') === 'true'
  const bridgeUrl = new URL(getBridgeUrl())
  return enabled && window.location.origin !== bridgeUrl.origin
}

export function primeFirebaseAuthBridgeWindow() {
  const width = 460
  const height = 720
  const left = Math.max(window.screenX + Math.round((window.outerWidth - width) / 2), 0)
  const top = Math.max(window.screenY + Math.round((window.outerHeight - height) / 2), 0)

  bridgeWindow = window.open(
    '',
    'huay-firebase-auth-bridge',
    `width=${width},height=${height},left=${left},top=${top},resizable=yes,scrollbars=yes`,
  )

  if (!bridgeWindow) {
    throw new Error('Firebase auth popup was blocked')
  }

  try {
    bridgeWindow.document.title = 'Loading OTP bridge...'
  } catch {
    // Ignore cross-origin access errors if popup already navigated.
  }

  return bridgeWindow
}

function openBridgeWindow() {
  const bridgeUrl = new URL(getBridgeUrl())
  bridgeUrl.searchParams.set('origin', window.location.origin)
  bridgeOrigin = bridgeUrl.origin

  const popup = (bridgeWindow && !bridgeWindow.closed) ? bridgeWindow : primeFirebaseAuthBridgeWindow()
  popup.location.replace(bridgeUrl.toString())
  bridgeWindow = popup

  return bridgeWindow
}

function waitForBridgeMessage<T extends BridgeMessage['type']>(
  expectedType: T,
  timeoutMs: number,
): Promise<Extract<BridgeMessage, { type: T }>> {
  return new Promise((resolve, reject) => {
    const timer = window.setTimeout(() => {
      cleanup()
      reject(new Error(`Firebase auth bridge timeout while waiting for ${expectedType}`))
    }, timeoutMs)

    const closeWatcher = window.setInterval(() => {
      if (bridgeWindow && bridgeWindow.closed) {
        cleanup()
        reject(new Error('Firebase auth popup was closed before completion'))
      }
    }, 500)

    const onMessage = (event: MessageEvent<BridgeMessage>) => {
      if (event.origin !== bridgeOrigin) {
        return
      }

      const message = event.data
      if (!message || message.source !== BRIDGE_SOURCE) {
        return
      }

      if (message.type === 'ERROR') {
        cleanup()
        reject(new Error(message.payload?.message || 'Firebase auth bridge failed'))
        return
      }

      if (message.type === expectedType) {
        cleanup()
        resolve(message as Extract<BridgeMessage, { type: T }>)
      }
    }

    const cleanup = () => {
      window.clearTimeout(timer)
      window.clearInterval(closeWatcher)
      window.removeEventListener('message', onMessage)
    }

    window.addEventListener('message', onMessage)
  })
}

export async function sendOTPViaFirebaseAuthBridge(options: SendOptions) {
  const popup = openBridgeWindow()

  await waitForBridgeMessage('READY', options.timeoutMs ?? 15000)

  popup.postMessage({
    source: CLIENT_SOURCE,
    type: 'START_PHONE_AUTH',
    payload: {
      tenantId: options.tenantId,
      phoneNumber: options.phoneNumber,
      apiBaseUrl: options.apiBaseUrl || '',
    },
  }, bridgeOrigin)

  const result = await waitForBridgeMessage('PHONE_AUTH_SENT', options.timeoutMs ?? 60000)
  const verificationId = result.payload?.verificationId

  if (verificationId) {
    savePersistedBridgeSession({
      tenantId: options.tenantId,
      phoneNumber: options.phoneNumber,
      apiBaseUrl: options.apiBaseUrl || '',
      verificationId,
      createdAt: Date.now(),
    })

    // Close the popup after OTP is sent. Verification can reopen it later.
    window.setTimeout(() => {
      closeFirebaseAuthBridge()
    }, 250)
  }

  return result
}

export async function verifyOTPViaFirebaseAuthBridge(otpCode: string, timeoutMs = 30000) {
  const persistedSession = loadPersistedBridgeSession()
  const popup = (bridgeWindow && !bridgeWindow.closed) ? bridgeWindow : openBridgeWindow()

  await waitForBridgeMessage('READY', 15000)

  popup.postMessage({
    source: CLIENT_SOURCE,
    type: 'VERIFY_PHONE_OTP',
    payload: {
      otpCode,
      verificationId: persistedSession?.verificationId,
      tenantId: persistedSession?.tenantId,
      apiBaseUrl: persistedSession?.apiBaseUrl || '',
    },
  }, bridgeOrigin)

  const result = await waitForBridgeMessage('PHONE_AUTH_VERIFIED', timeoutMs)
  clearPersistedBridgeSession()
  return result
}

export function closeFirebaseAuthBridge() {
  if (bridgeWindow && !bridgeWindow.closed) {
    bridgeWindow.postMessage({ source: CLIENT_SOURCE, type: 'CLOSE' }, bridgeOrigin)
    bridgeWindow.close()
  }

  bridgeWindow = null
}
